import { Suspense, lazy, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthToken } from "@convex-dev/auth/react";

import { ChatWindow } from "@/components/chat/ChatWindow";
import {
  WorkflowDetail,
  type WorkflowTab,
} from "@/components/workflows/WorkflowDetail";
import {
  WorkflowDetailLoading,
  WorkflowNotFound,
} from "@/components/workflows/WorkflowDetailFallbacks";
import { useSpecById } from "@/hooks/useSpecById";
import { useWorkflowPauseToggle } from "@/hooks/useWorkflowPauseToggle";
import { buildSpecSlug, parseSpecIdFromSlug } from "@domain/praxis-specs";
import type { Id } from "@convex/_generated/dataModel";

// The embedded workbench tabs — the SAME thread-keyed components the chat
// right panel mounts (lean embed path, workflows-dedicated-ui re-scope
// 2026-07-16): one implementation renders workflow state on both surfaces.
// Lazy for the same chunk-hygiene reasons as TaskPanel (graph + praxis-core
// imports stay out of the eager bundle).
const SpecTab = lazy(() =>
  import("@/components/tasks/SpecTab").then((m) => ({ default: m.SpecTab })),
);
const ExecutionTab = lazy(() =>
  import("@/components/tasks/ExecutionTab").then((m) => ({
    default: m.ExecutionTab,
  })),
);

export const Route = createFileRoute("/_authenticated/workflows/$slug")({
  component: WorkflowDetailRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    // Which detail tab is active. "diagram"/"runs" are legacy values from the
    // pre-embed three-tab schema — "runs" maps to Activity (run history);
    // "diagram" now maps to Overview, whose SpecTab hosts the Specification/
    // Diagram/References sub-tabs. Anything else → overview.
    tab:
      search.tab === "activity" || search.tab === "runs"
        ? ("activity" as WorkflowTab)
        : ("overview" as WorkflowTab),
  }),
});

function TabLoading() {
  return (
    <div className="flex h-full items-center justify-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
      Loading…
    </div>
  );
}

/**
 * The per-workflow detail route — thin by design: Convex (`useSpecById`),
 * auth, and navigation live here; the two-pane shell is pure
 * (`WorkflowDetail`) and its pane content arrives as slots.
 *
 * The route param is a vanity slug — `<kebab(title)>~<specId>` — resolved
 * ONLY by its id tail (`parseSpecIdFromSlug`), so links survive workflow
 * renames; a stale/bare-id slug replace-redirects to the canonical shape.
 *
 * `useSpecById` is tri-state: `undefined` → loading, `null` → not-found /
 * foreign-owned / malformed id (ownership never leaks existence), else
 * `{ spec, version }`. Specs are thread-bound, so the embedded chat is the
 * spec's own thread.
 */
function WorkflowDetailRoute() {
  const { slug } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const authToken = useAuthToken();
  const specId = parseSpecIdFromSlug({ slug });
  const { result } = useSpecById({ specId });
  const pauseToggle = useWorkflowPauseToggle({
    specId: result != null ? result.spec._id : null,
  });

  // Canonical-slug redirect: a bare-id or renamed-title link resolves fine
  // (the id tail is the key) but the URL is rewritten in place so what the
  // user copies next is the canonical shape.
  const canonicalSlug =
    result != null
      ? buildSpecSlug({ title: result.spec.title, specId: result.spec._id })
      : null;
  useEffect(() => {
    if (canonicalSlug === null || canonicalSlug === slug) return;
    void navigate({
      to: "/workflows/$slug",
      params: { slug: canonicalSlug },
      search: { tab },
      replace: true,
    });
  }, [canonicalSlug, slug, tab, navigate]);

  if (result === undefined) {
    return <WorkflowDetailLoading />;
  }

  if (result === null) {
    return <WorkflowNotFound onBack={() => void navigate({ to: "/workflows" })} />;
  }

  const threadId = result.spec.threadId as Id<"threads"> | undefined;

  return (
    <WorkflowDetail
      spec={result.spec}
      activeTab={tab}
      onTabChange={(nextTab) => {
        void navigate({
          to: "/workflows/$slug",
          params: { slug },
          search: { tab: nextTab },
        });
      }}
      onBack={() => void navigate({ to: "/workflows" })}
      pauseControl={{
        pending: pauseToggle.pending,
        error: pauseToggle.error,
        onPause: () => void pauseToggle.pauseWorkflow(),
        onResume: () => void pauseToggle.activateWorkflow(),
      }}
      chatSlot={
        threadId !== undefined ? (
          <ChatWindow
            className="flex-1 overflow-hidden"
            threadId={threadId}
            authToken={authToken}
          />
        ) : undefined
      }
      overviewSlot={
        threadId !== undefined ? (
          <Suspense fallback={<TabLoading />}>
            <SpecTab threadId={threadId} />
          </Suspense>
        ) : undefined
      }
      activitySlot={
        threadId !== undefined ? (
          <Suspense fallback={<TabLoading />}>
            <ExecutionTab threadId={threadId} />
          </Suspense>
        ) : undefined
      }
    />
  );
}
